import { consume } from '@lit/context';
import { localized } from '@weavedev/lit-i18next';
import { isAxiosError } from 'axios';
import {
  html,
  LitElement,
  nothing,
  PropertyValueMap,
  TemplateResult,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import {
  ConfigurationDto,
  ConfiguratorLayoutEnum,
  ConfiguratorSummaryDto,
  getShowConfiguratorSummary,
} from '@/api-sdk';
import { FgPriceDetails } from '@/components';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  personalisation,
  PersonalisationType,
  printExample,
  PrintExampleContextType,
} from '@/context';
import {
  ActionEvent,
  IntentActionEvent,
  IntentToEvent,
  MessageTypes,
} from '@/types';
import {
  getParamsObjectFromQueryString,
  optionParamsWithQuantities,
  saveConfiguration,
} from '@/utils';

import '../price-details/fg-price-details';
import './mobile/fg-configurator-mobile-summary';
import './summary/fg-configurator-actions-top-bottom';
import './summary/fg-configurator-summary';

@customElement('fg-configurator-actions')
@localized()
export class FgConfiguratorActions extends LitElement {
  fgPriceDetailsDialogRef: Ref<FgPriceDetails> = createRef();

  /**
   * Product code, and is used to fetch the price breakdown data
   *
   * @property {string} product
   */
  @property({ type: String })
  product = '-1';

  /**
   * Query string containing the configuration data, and is used to fetch te price breakdown data
   */
  @property({ type: String })
  queryString = '';

  /**
   * Object with configuration data see: {@link ConfigurationDto}, received from the API
   *
   * @property {ConfigurationDto} configuration
   */
  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  /**
   * Custom quantity validation based on the quantity validation state\
   *
   * @property {boolean} quantityValidated
   */
  @property({ type: Boolean })
  quantityValidated = false;

  /**
   * Event to save the configuration, and is used to save the configuration and set the right checkout mode in CE
   *
   * @property {string} eventToSave
   */
  @property({ type: String })
  eventToSave?: string;

  /**
   * Boolean to determine if the print option is selected as printed or unprinted
   */
  @property({ type: Boolean })
  unprinted = false;

  /**
   * Promise that is used to fetch the price breakdown data
   */
  @state()
  summaryPromise: Promise<ConfiguratorSummaryDto> | null = null;

  @state()
  summaryController: AbortController | null = null;

  /**
   * Object containing the price breakdown data, and is used to render the price breakdown data & summary
   */
  @state()
  summaryData?: ConfiguratorSummaryDto;

  /**
   * Loading state, and is used to render a loading state
   */
  @state()
  loading = false;

  /**
   * Error state, and is used to render an error messages
   */
  @state()
  error = false;

  /**
   * Action event, and is used to trigger the right checkout mode in CRE
   */
  @state()
  actionEvent?: ActionEvent;

  @state()
  draftValidationMessage: { message: string; type: MessageTypes } | undefined;

  @state()
  index = 0;

  @state()
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext: PersonalisationType[] = [];

  @consume({ context: printExample.context, subscribe: true })
  printExampleContext?: PrintExampleContextType;

  protected async willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): Promise<void> {
    if (_changedProperties.has('eventToSave') && this.eventToSave) {
      const event: { [key: string]: ActionEvent } = {
        'handle-add-to-cart': 'add-to-cart',
        'handle-add-to-quotation': 'add-to-quotation',
        'handle-request-print-proof': 'request-print-proof',
        'handle-update-cart': 'update-cart',
      };

      await this.saveConfiguration({ eventName: event[this.eventToSave] });
    }

    if (_changedProperties.has('queryString')) {
      const { salable, product } = this.configuratorContext,
        { loggedIn, layout } = this.globalContext;

      if (!product || ((!loggedIn || !salable) && layout === 'wholesale')) {
        return;
      }

      this.getSummary(this.queryString);
    }
  }

  async getSummary(queryString: string) {
    this.loading = true;
    this.error = false;

    const summaryParams = getParamsObjectFromQueryString(queryString),
      optionParams = optionParamsWithQuantities(summaryParams),
      { configurationId, product, colorCollapsible, showProductionSpeedStep } =
        this.configuratorContext,
      { locale, vatRate, layout, country, debtor } = this.globalContext;

    if (this.summaryPromise !== null && this.summaryController !== null) {
      this.summaryController.abort();
      this.summaryPromise = null;
      this.summaryData = undefined;
    }

    this.summaryController = new AbortController();
    this.summaryPromise = getShowConfiguratorSummary(
      {
        code: product,
        params: {
          colorCollapsible: colorCollapsible,
          configurationId: configurationId || undefined,
          locale: locale,
          vatRate: vatRate,
          layout:
            layout === 'retail'
              ? ConfiguratorLayoutEnum.retail
              : ConfiguratorLayoutEnum.wholesale,
          country: country,
          debtor: debtor,
          showProductionSpeedStep:
            Number(showProductionSpeedStep) === 1 ? 0 : 1,
          ...optionParams,
        },
      },
      {
        signal: this.summaryController.signal,
      },
    );

    try {
      this.summaryData = await this.summaryPromise;
    } catch (error) {
      if (isAxiosError(error) && error?.name === 'CanceledError') {
        return;
      }
      this.error = true;
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  renderSummary(): TemplateResult {
    switch (this.globalContext.layout) {
      case 'retail':
        return html`
          <fg-configurator-actions-top-bottom
            part=${this.globalContext.layout}
            .configuration=${this.configuration}
            .expectedDeliveryDate=${this.summaryData?.priceBreakdown
              ?.expectedDeliveryDate}
            .unprinted=${this.unprinted}
            .error=${this.error}
            .loading=${this.loading}
            .priceBreakDownData=${this.summaryData?.priceBreakdown || undefined}
            .quantityValidated=${this.quantityValidated}
            .productionSpeedStep=${this.summaryData?.productionSpeedStep}
            @open-price-details=${this.openPriceDetailsDialog}
            @intent-to=${(event: CustomEvent<IntentToEvent>) =>
              this.intentToSave({ eventName: event.detail.eventName })}
            @trigger-validation-events=${this.triggerValidationMessages}
          ></fg-configurator-actions-top-bottom>
        `;
      case 'wholesale':
        return html`
          <fg-configurator-mobile-summary
            part="wholesale"
            .priceBreakdown=${this.summaryData?.priceBreakdown || undefined}
            .isLoading=${this.loading}
            .totalsExclVat=${this.summaryData?.priceBreakdown?.totals
              ?.excludingVat.formatted}
            @open-price-details=${this.openPriceDetailsDialog}
            @trigger-validation-events=${this.triggerValidationMessages}
          ></fg-configurator-mobile-summary>
          ${this.configuratorContext.salable && this.globalContext.loggedIn
            ? html`
                <fg-configurator-summary
                  part=${this.globalContext.layout}
                  .configuration=${this.configuration}
                  .error=${this.error}
                  .loading=${this.loading}
                  .priceBreakdown=${this.summaryData?.priceBreakdown ||
                  undefined}
                  .quantityValidated=${this.quantityValidated}
                  .customerId=${this.globalContext.customerId}
                  @intent-to=${(event: CustomEvent<IntentToEvent>) =>
                    this.intentToSave({ eventName: event.detail.eventName })}
                  @trigger-validation-events=${this.triggerValidationMessages}
                  @show-draft-validation-message=${(
                    event: CustomEvent<{ message: string; type: MessageTypes }>,
                  ) => (this.draftValidationMessage = event.detail)}
                  @open-price-details=${this.openPriceDetailsDialog}
                ></fg-configurator-summary>
              `
            : nothing}
        `;
    }
  }

  render() {
    const { layout } = this.globalContext;
    return html`
      ${layout === 'wholesale'
        ? html`
            <fg-message-snack-bar
              ?show=${!!this.draftValidationMessage}
              part=${layout}
              .message="${this.draftValidationMessage?.message}"
              .type=${this.draftValidationMessage?.type || MessageTypes.SUCCESS}
              @on-close=${() => (this.draftValidationMessage = undefined)}
            ></fg-message-snack-bar>
          `
        : nothing}
      ${this.renderSummary()}
      <fg-price-details
        part=${layout}
        ${ref(this.fgPriceDetailsDialogRef)}
        .priceBreakdown=${this.summaryData?.priceBreakdown}
      ></fg-price-details>
    `;
  }

  openPriceDetailsDialog() {
    if (!this.loading) {
      this.fgPriceDetailsDialogRef.value?.openDialog();
    }
  }

  async intentToSave({ eventName }: { eventName: IntentActionEvent }) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        composed: true,
      }),
    );
  }

  async saveConfiguration({ eventName }: { eventName: ActionEvent }) {
    if (!this.configuration) {
      return;
    }

    this.error = false;
    this.loading = true;
    try {
      await saveConfiguration({
        globalContext: this.globalContext,
        personalisationContext: this.personalisationContext,
        printExampleContext: this.printExampleContext?.service,
        configuration: this.configuration,
        callback: configurationId => {
          this.dispatchEvent(
            new CustomEvent(eventName, {
              composed: true,
              detail: {
                configuration: configurationId,
              },
            }),
          );
        },
      });
    } catch {
      this.error = true;
      this.loading = false;
    } finally {
      this.actionEvent = undefined;
      this.dispatchEvent(
        new CustomEvent('reset-action-event', { composed: true }),
      );
    }
  }
  /**
   * @function triggerValidationMessages
   * @param event
   */
  triggerValidationMessages(event: CustomEvent<{ actionEvent: ActionEvent }>) {
    const { actionEvent } = event.detail;

    this.dispatchEvent(
      new CustomEvent('trigger-validation-messages', {
        composed: true,
        detail: {
          actionEvent: actionEvent,
        },
      }),
    );
  }
}
